import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, MessageSquare, Share2 } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const AnnouncementManagement = ({ user, onLogout }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    target_audience: "all",
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get("/announcements");
      setAnnouncements(response.data.announcements);
    } catch (error) {
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const createAnnouncement = async () => {
    if (!formData.title || !formData.message) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      await api.post("/announcements", formData);
      toast.success("Announcement created successfully!");
      fetchAnnouncements();
      setOpen(false);
      setFormData({ title: "", message: "", target_audience: "all" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create announcement");
    }
  };

  const shareOnWhatsApp = (announcement) => {
    if (announcement.whatsapp_link) {
      window.open(announcement.whatsapp_link, "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-heading text-slate-900" data-testid="announcement-management-title">
              Announcements
            </h1>
            <p className="text-slate-600 mt-2">Create and share announcements with students</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-announcement-button" className="h-12 rounded-full bg-primary hover:bg-primary/90 font-medium">
                <Plus className="w-5 h-5 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Create Announcement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="announcement-title">Title</Label>
                  <Input
                    id="announcement-title"
                    data-testid="announcement-title-input"
                    placeholder="Enter announcement title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="announcement-message">Message</Label>
                  <Textarea
                    id="announcement-message"
                    data-testid="announcement-message-input"
                    placeholder="Enter your message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="min-h-[120px] rounded-xl"
                  />
                </div>

                <Button
                  data-testid="submit-announcement-button"
                  onClick={createAnnouncement}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium"
                >
                  Create & Share
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : announcements.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No announcements yet. Create your first one!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {announcements.map((announcement, index) => (
              <motion.div
                key={announcement.announcement_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
                data-testid={`announcement-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold font-heading text-slate-900">{announcement.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {new Date(announcement.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {announcement.whatsapp_link && (
                    <Button
                      data-testid={`share-whatsapp-${index}`}
                      onClick={() => shareOnWhatsApp(announcement)}
                      className="bg-[#25D366] hover:bg-[#128C7E] text-white h-10 px-4 rounded-full font-medium flex items-center gap-2"
                    >
                      <Share2 className="w-4 h-4" />
                      Share on WhatsApp
                    </Button>
                  )}
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{announcement.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
};

export default AnnouncementManagement;
